import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import {
  BrandMark,
  Button,
  ButtonRouterLink,
  Card,
  MonoLabel,
  SectionCrosshairs,
  SectionHead,
  Tag,
  ThemeToggle
} from '../components/primitives';
import { scrollToLandingSection } from '../lib/landingScroll';
import { useTheme } from '../lib/theme';
import './Landing.css';
import { Hero } from './LandingHero';
import { LandingPricing } from './landing/LandingPricing';

const LP_NAV_LINKS = [
  { sectionId: 'what', label: 'What it is' },
  { sectionId: 'roles', label: 'Roles' },
  { sectionId: 'agents', label: 'Agents' },
  { sectionId: 'how', label: 'How it works' },
  { sectionId: 'pricing', label: 'Pricing' }
] as const;

function navigateToLandingSection(
  sectionId: string,
  e: MouseEvent<HTMLAnchorElement>
) {
  e.preventDefault();
  scrollToLandingSection(sectionId);
}

const MENU_ICON = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
    focusable="false"
    className="lp-nav-menu-ico"
  >
    <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" strokeLinecap="round" />
  </svg>
);

function Chrome() {
  const [theme, setTheme] = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    const onClick = (e: Event) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setNavOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
    };
  }, [navOpen]);

  return (
    <header className="lp-chrome">
      <div className="pn-page lp-chrome-inner">
        <div ref={menuRef} className="lp-nav-menu pn-popover-wrap">
          <Button
            type="button"
            variant="header"
            className="lp-nav-menu-btn"
            aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={navOpen}
            aria-controls={menuId}
            aria-haspopup="menu"
            data-testid="lp-nav-menu"
            onClick={() => setNavOpen((open) => !open)}
          >
            {MENU_ICON}
          </Button>
          {navOpen ? (
            <div
              id={menuId}
              className="pn-popover pn-popover-start lp-nav-menu-panel"
              role="menu"
              aria-label="Primary"
            >
              {LP_NAV_LINKS.map((link) => (
                <a
                  key={link.sectionId}
                  href="#/landing"
                  role="menuitem"
                  className="lp-nav-menu-item"
                  onClick={(e) => {
                    navigateToLandingSection(link.sectionId, e);
                    setNavOpen(false);
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          ) : null}
        </div>
        <a
          href="#/landing"
          className="lp-brand"
          onClick={(e) => navigateToLandingSection('top', e)}
        >
          <BrandMark className="lp-brand-mark" />
          <span className="lp-brand-word">Penrose</span>
          <MonoLabel size="md" tracking="wide" className="lp-brand-ser">v0.4 · build 1142</MonoLabel>
        </a>
        <nav className="lp-nav" aria-label="Primary">
          {LP_NAV_LINKS.map((link) => (
            <a
              key={link.sectionId}
              href="#/landing"
              onClick={(e) => navigateToLandingSection(link.sectionId, e)}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <span className="lp-grow" />
        <div className="lp-chrome-actions">
          <ThemeToggle theme={theme} onChange={setTheme} />
          <ButtonRouterLink to="/sign-in">Sign in</ButtonRouterLink>
          <ButtonRouterLink to="/sign-up" variant="primary">Start free</ButtonRouterLink>
        </div>
      </div>
    </header>
  );
}


function ProblemSection() {
  return (
    <section className="pn-section" id="problem">
      <div className="pn-page">
        <SectionCrosshairs />
        <SectionHead marker="§ 01" title="Your team spends half the day looking things up." tag="The problem" />

        <div className="lp-loop">
          <Card className="lp-loop-card lp-loop-card-quotes">
            <MonoLabel size="md" tracking="wider" tone="accent">// asked every day</MonoLabel>
            <ul className="lp-quotes-list">
              <li>What am I supposed to be working on?</li>
              <li>Where's the latest PRD?</li>
              <li>Did the designer build a prototype yet?</li>
              <li>Where are the acceptance criteria?</li>
              <li>What's the format for this kind of doc?</li>
              <li>Does my agent know any of this?</li>
            </ul>
          </Card>
          <Card className="lp-loop-card lp-loop-card-tax">
            <MonoLabel size="md" tracking="wider" tone="accent">// the cost</MonoLabel>
            <div className="lp-stat tnum">10+</div>
            <MonoLabel size="md" tracking="wide">tools your team's AI can't read across</MonoLabel>
            <p className="lp-card-p">
              Connectors let an agent <em>fetch</em> a Linear issue. They don't tell it if the
              spec is current, if the design matches, or if QA signed off. Pulling files isn't
              the same as sharing the work.
            </p>
          </Card>
        </div>

        <div className="lp-loop-tail">
          Engineers get the worst of it. Tasks, notes, and agent rules live <strong>inside the
          repo and branch</strong>. Switch branches and half your context is gone. You didn't
          lose the code — <strong>you lost everything around it</strong>.
        </div>
      </div>
    </section>
  );
}

function WhatSection() {
  return (
    <section className="pn-section" id="what">
      <div className="pn-page">
        <SectionCrosshairs />
        <SectionHead marker="§ 02" title="What Penrose is." tag="Two surfaces, one workspace" />

        <div className="lp-def">
          <div className="lp-def-lede">
            One workspace. <span className="lp-accent">One set of rules.</span> One source of truth — for the whole team.
          </div>
          <div className="lp-pillars">
            <Pillar marker="§ 02.1 · Documents" title="Specs, tasks, signoffs">
              Specs, design docs, tasks, test plans, signoffs — all in one place. Same shape every time. Statuses your whole team agrees on.
            </Pillar>
            <Pillar marker="§ 02.2 · Role rules" title="Set once, used everywhere">
              Pick a role: Design, Product, Engineering, QA. Set the rules. Every agent on the team reads them — no more <code>CLAUDE.md</code> drifting on every laptop.
            </Pillar>
            <Pillar marker="§ 02.3 · IDE + web" title="Both ship day one">
              Penrose IDE for people who live in code. Penrose web for everyone else. Same documents, same tasks, same agents. No silos.
            </Pillar>
            <Pillar marker="§ 02.4 · Workflows" title="Rules per status">
              An agent reviewing a spec gets different instructions than one shipping it. Set the workflow once. Every agent follows it.
            </Pillar>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillar({ marker, title, children }: { marker: string; title: string; children: React.ReactNode }) {
  return (
    <div className="lp-pillar">
      <MonoLabel size="md" tracking="wider" tone="accent">{marker}</MonoLabel>
      <h4 className="lp-pillar-h">{title}</h4>
      <p className="lp-pillar-p">{children}</p>
    </div>
  );
}

function RolesSection() {
  return (
    <section className="pn-section" id="roles">
      <div className="pn-page">
        <SectionCrosshairs />
        <SectionHead marker="§ 03" title="What each role gets." tag="For the whole team" />

        <div className="lp-roles">
          <Role badge="DS" name="Designers" stop="Stop hunting" gain="Spec → mock → ship · one trail">
            The PRD, the open tickets that need design, the past mocks, the prototype links — all in one place. Your design agent works from that, not a folder of screenshots.
          </Role>
          <Role badge="PM" name="Product" stop="Stop repeating yourself" gain="One spec · everyone aligned">
            The spec is the source of truth. Tasks link to it. Test plans inherit from it. Designs reference it. Update it once — everything downstream sees the change.
          </Role>
          <Role badge="EN" name="Engineers" stop="Stop re-briefing your agent" gain="BYO agent · shared context" accent>
            Keep the coding agent you like. Get the spec, design, and acceptance criteria in the same workspace as the code — already shaped for your agent to read.
          </Role>
          <Role badge="QA" name="QA" stop="Get a real seat" gain="Signoff authority · not a bolt-on">
            QA is a first-class role: its own agent, its own docs (test plans, regression reports, signoffs), and the authority to block a status change until tests pass. Almost no one ships this.
          </Role>
        </div>
      </div>
    </section>
  );
}

function Role({
  badge, name, stop, gain, accent, children
}: {
  badge: string; name: string; stop: string; gain: string; accent?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="lp-role">
      <div className="lp-role-head">
        <span className={['lp-role-ic', accent ? 'lp-role-ic-ac' : ''].filter(Boolean).join(' ')}>{badge}</span>
        <h4 className="lp-role-h">{name}</h4>
      </div>
      <MonoLabel size="md" tracking="wide">{stop}</MonoLabel>
      <p className="lp-role-p">{children}</p>
      <span className="lp-role-gain">{gain}</span>
    </div>
  );
}

function AgentsSection() {
  return (
    <section className="pn-section" id="agents">
      <div className="pn-page">
        <SectionCrosshairs />
        <SectionHead marker="§ 04" title="Bring your coding agent." tag="Works with what you have" />

        <div className="lp-byo">
          <p className="lp-byo-lede">
            Penrose is the <em>workspace</em>. The agent is yours. Use whatever you already
            like — Penrose works the same with any of them.
          </p>

          <div className="lp-agents">
            <Agent name="Claude Code" vendor="Anthropic · CLI" />
            <Agent name="Codex" vendor="OpenAI · CLI / IDE" />
            <Agent name="Gemini" vendor="Google · CLI" />
            <Agent name="Cline" vendor="Open source · VS Code" />
            <Agent name="Continue" vendor="Open source · VS Code" />
            <Agent name="Copilot" vendor="GitHub · IDE" />
          </div>

          <p className="lp-byo-foot">
            <MonoLabel size="lg" tracking="wide" tone="accent" className="lp-byo-foot-k">// Note:</MonoLabel>
            Cursor and Windsurf agents only run inside their own apps — they can't move with
            you. If you use one, you'll want a portable agent alongside Penrose.
          </p>
        </div>
      </div>
    </section>
  );
}

function Agent({ name, vendor }: { name: string; vendor: string }) {
  return (
    <div className="lp-agent">
      <span className="lp-agent-n">{name}</span>
      <MonoLabel size="sm" tracking="default">{vendor}</MonoLabel>
      <span className="lp-agent-ok">// supported</span>
    </div>
  );
}

function HowSection() {
  return (
    <section className="pn-section" id="how">
      <div className="pn-page">
        <SectionCrosshairs />
        <SectionHead marker="§ 05" title="How agents get the right context." tag="Role-aware MCP" />

        <div className="lp-moat">
          <div className="lp-moat-text">
            <h3 className="lp-moat-h">Every agent. Same rules.</h3>
            <p className="lp-moat-p">
              Every agent session knows <strong>who you are</strong>, <strong>which org you're in</strong>,
              and <strong>what your role is</strong>. Your role's rules and the document's workflow are
              passed in <span className="lp-accent">automatically</span> — no file someone forgot to update.
            </p>
            <p className="lp-moat-p">
              New hires get the same rules as everyone else on day one. Standards are versioned.
              Permissions are checked everywhere — the database, the UI, the agent.
              <strong> You set the rules. Penrose enforces them.</strong>
            </p>
            <MonoLabel as="div" size="xl" tracking="default" className="lp-moat-note">
              // Linear can't add designers and engineers without breaking Linear. Cursor
              can't add PMs without breaking Cursor. We started here on purpose.
            </MonoLabel>
          </div>

          <div className="lp-diagram">
            <Tag className="lp-diagram-label">FIG.04 · the stack</Tag>
            <DiagramLayer tag="L01" title="Identity · Org · Role" sub="who you are · where · what you can do" />
            <div className="lp-diagram-conn" aria-hidden="true" />
            <DiagramLayer tag="L02" title="Your work" sub="documents · tasks · statuses" />
            <div className="lp-diagram-conn" aria-hidden="true" />
            <DiagramLayer tag="L03" title="Role-aware MCP" sub="scoped to your role automatically" accent />
            <div className="lp-diagram-conn" aria-hidden="true" />
            <div className="lp-diagram-fan">
              <span>Claude</span>
              <span>Codex</span>
              <span>Gemini</span>
              <span>…</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DiagramLayer({
  tag, title, sub, accent
}: { tag: string; title: string; sub: string; accent?: boolean }) {
  return (
    <div className={['lp-diagram-layer', accent ? 'lp-diagram-layer-ac' : ''].filter(Boolean).join(' ')}>
      <MonoLabel size="sm" tracking="wide" tone={accent ? 'accent' : 'muted'}>{tag}</MonoLabel>
      <div>
        <div className="lp-diagram-ttl">{title}</div>
        <MonoLabel as="div" size="sm" tracking="snug">{sub}</MonoLabel>
      </div>
    </div>
  );
}

function WhySection() {
  return (
    <section className="pn-section pn-section-tight" id="why">
      <div className="pn-page">
        <SectionHead marker="§ 06" title="Why now." tag="April 2026 · signals" />

        <div className="lp-why">
          <WhyCard signal="Signal · 01" title="Every role already uses AI">
            Designers, PMs, engineers, QA — they all have a tool. The problem now isn't getting AI in their hands. It's getting their work to line up.
          </WhyCard>
          <WhyCard signal="Signal · 02" title="&ldquo;Builder PM&rdquo; is mainstream">
            Cross-functional, cross-tool teams have a name now. The audience for one shared workspace is real today — not in 18 months.
          </WhyCard>
          <WhyCard signal="Signal · 03" title="The closest competitor shut down">
            Roo Code was the only VS Code extension building role-based agent modes. They announced shutdown for May 2026. The space is open.
          </WhyCard>
        </div>
      </div>
    </section>
  );
}

function WhyCard({ signal, title, children }: { signal: string; title: string; children: React.ReactNode }) {
  return (
    <div className="lp-why-card">
      <MonoLabel size="lg" tracking="widest" tone="accent">{signal}</MonoLabel>
      <h4 className="lp-why-h">{title}</h4>
      <p className="lp-why-p">{children}</p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="lp-foot">
      <div className="pn-page lp-foot-grid">
        <div className="lp-foot-brand">
          <div className="lp-foot-word">Penrose</div>
          <MonoLabel size="sm" tracking="default" className="lp-foot-sub">
            v0.4 · build 1142 · For teams that ship together
          </MonoLabel>
        </div>
        <div className="lp-foot-links">
          <FooterCol heading="Product">
            <a href="#what">What it is</a>
            <a href="#roles">Roles</a>
            <a href="#agents">Agents</a>
            <a href="#how">Architecture</a>
          </FooterCol>
          <FooterCol heading="For">
            <a href="#roles">Designers</a>
            <a href="#roles">Product</a>
            <a href="#roles">Engineers</a>
            <a href="#roles">QA</a>
          </FooterCol>
          <FooterCol heading="Resources">
            <a href="#pitch">Pitch</a>
            <a href="#docs">Docs</a>
            <a href="#mcp">MCP</a>
            <a href="#changelog">Changelog</a>
          </FooterCol>
          <FooterCol heading="Company">
            <a href="#about">About</a>
            <a href="#careers">Careers</a>
            <a href="#contact">Contact</a>
            <a href="#legal">Legal</a>
          </FooterCol>
        </div>
        <div className="lp-foot-meta">
          <MonoLabel as="div" size="sm" tracking="wide">© 2026 Penrose Inc</MonoLabel>
          <MonoLabel as="div" size="sm" tracking="wide">OPS.01 · Sig online</MonoLabel>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="lp-foot-col">
      <span className="lp-foot-col-h">{heading}</span>
      {children}
    </div>
  );
}

export function Landing() {
  return (
    <>
      <Chrome />
      <main id="top">
        <Hero />
        <ProblemSection />
        <WhatSection />
        <RolesSection />
        <AgentsSection />
        <HowSection />
        <WhySection />
        <LandingPricing />
        <Footer />
      </main>
    </>
  );
}
